/**
 * Client service for sector operations workforce (parties and workers).
 * R39 A3.a (plan docs/plans/sector-operations.md §2 SO-D11, SO-P49; §4 ops_workers, ops_parties; §6 A3.a).
 * Wraps callables for writes and listens to firestore ops_parties / ops_workers for reads.
 */

import {
  collection,
  query,
  where,
  onSnapshot,
  type Unsubscribe,
  type QueryConstraint,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  OpsSector,
  OpsParty,
  OpsWorker,
  OpsWorkerStatus,
  CreatePartyInput,
  UpdatePartyInput,
  SetPartyMembersInput,
  CreateWorkerInput,
  UpdateWorkerInput,
  ArchiveWorkerInput,
  AddGangInput,
} from '../types/ops'

export async function createParty(
  input: CreatePartyInput,
): Promise<{ partyId: string }> {
  return callable<CreatePartyInput, { partyId: string }>('createParty', input)
}

export async function updateParty(
  input: UpdatePartyInput,
): Promise<{ partyId: string; success: boolean }> {
  return callable<UpdatePartyInput, { partyId: string; success: boolean }>('updateParty', input)
}

export async function setPartyMembers(
  input: SetPartyMembersInput,
): Promise<{ partyId: string; memberCount: number; updatedWorkerCount: number }> {
  return callable<
    SetPartyMembersInput,
    { partyId: string; memberCount: number; updatedWorkerCount: number }
  >('setPartyMembers', input)
}

export async function createWorker(
  input: CreateWorkerInput,
): Promise<{ workerId: string; workerCode: string; replayed?: boolean }> {
  return callable<
    CreateWorkerInput,
    { workerId: string; workerCode: string; replayed?: boolean }
  >('createWorker', input)
}

export async function updateWorker(
  input: UpdateWorkerInput,
): Promise<{ workerId: string; success: boolean }> {
  return callable<UpdateWorkerInput, { workerId: string; success: boolean }>('updateWorker', input)
}

export async function archiveWorker(
  input: ArchiveWorkerInput,
): Promise<{ workerId: string; success: boolean }> {
  return callable<ArchiveWorkerInput, { workerId: string; success: boolean }>(
    'archiveWorker',
    input,
  )
}

export async function addGang(
  input: AddGangInput,
): Promise<{ count: number; opId: string; workerCodes: string[]; replayed?: boolean }> {
  return callable<
    AddGangInput,
    { count: number; opId: string; workerCodes: string[]; replayed?: boolean }
  >('addGang', input)
}

export function subscribeToParties(
  workspaceId: string,
  sector: OpsSector,
  callback: (parties: OpsParty[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const partiesRef = collection(firestoreDb, 'ops_parties')
  const q = query(
    partiesRef,
    where('workspaceId', '==', workspaceId),
    where('sector', '==', sector),
  )

  return onSnapshot(
    q,
    (snap) => {
      const parties: OpsParty[] = snap.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          workspaceId: data.workspaceId,
          sector: data.sector,
          kind: data.kind,
          name: data.name,
          contact: data.contact,
          scope: data.scope,
          externalRef: data.externalRef,
          memberUids: Array.isArray(data.memberUids) ? data.memberUids : [],
          archived: data.archived === true,
          createdAt: data.createdAt,
          createdBy: data.createdBy,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy,
        }
      })
      callback(parties)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeToWorkers(
  workspaceId: string,
  options: { sector?: OpsSector; partyId?: string; status?: OpsWorkerStatus },
  callback: (workers: OpsWorker[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const workersRef = collection(firestoreDb, 'ops_workers')
  const constraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (options.sector) {
    constraints.push(where('sector', '==', options.sector))
  }
  if (options.partyId) {
    constraints.push(where('partyId', '==', options.partyId))
  }
  if (options.status) {
    constraints.push(where('status', '==', options.status))
  }

  const q = query(workersRef, ...constraints)

  return onSnapshot(
    q,
    (snap) => {
      const workers: OpsWorker[] = snap.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          workspaceId: data.workspaceId,
          sector: data.sector,
          displayName: data.displayName,
          workerCode: data.workerCode,
          partyId: data.partyId,
          trade: data.trade,
          skills: Array.isArray(data.skills) ? data.skills : [],
          placeIds: Array.isArray(data.placeIds) ? data.placeIds : [],
          phone: data.phone ?? undefined,
          memberUid: data.memberUid ?? undefined,
          status: data.status,
          partyMemberUids: Array.isArray(data.partyMemberUids) ? data.partyMemberUids : [],
          createdAt: data.createdAt,
          createdBy: data.createdBy,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy,
        }
      })
      callback(workers)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}
